// Shared API endpoint map for the HealthOS typed RPC client.
//
// This module owns the endpoint URL mapping (which path to call on the typed
// Hono client), the error labels, and the type annotations. Both web and
// mobile import these functions instead of duplicating the URL map.
//
// The seam: each function takes a `client: HonoClient` parameter (the typed
// Hono RPC client) instead of a token. Each client constructs its own client
// (auth, base URL, fetch wrapper, token refresh) and passes it in.
//
// Contract types are NOT hand-written here. They are re-exported from
// @healthos/schemas (the single source of truth for the API contract, per
// ADR-0010), so a server schema change surfaces as a compile error here rather
// than silently drifting.
import { api, } from "@healthos/schemas";
// TanStack Query's queryFn signal only fires on cancellation (unmount,
// invalidation, a superseding refetch) - it carries no timeout of its own, so
// a hung request (dropped connection, a proxy that never closes the socket)
// leaves the query pending forever with nothing to surface an error.
//
// The ceiling itself is applied by authedFetch, not composed here:
// AbortSignal.timeout()'s clock starts the instant it's created, but
// authedFetch still has to await getFreshBearerToken() first - OIDC silent
// renew can take up to 12s, the desktop host-auth handshake up to 100s -
// before the real network call even starts. Composing the timeout at this
// call site would burn most or all of its budget on a legitimate
// slow-but-successful auth refresh and abort an otherwise-healthy request.
// `timeoutMs` tells authedFetch to start the clock itself, right before the
// actual fetch. Callers that construct their own signal directly (e.g.
// brief-audio.ts's 90s download) don't set it and keep full control of their
// own timing.
export const QUERY_TIMEOUT_MS = 15_000;
// Hono's ClientRequestOptions.init is a plain RequestInit, and
// exactOptionalPropertyTypes rejects `signal: undefined` (DOM's RequestInit
// only allows AbortSignal | null). Omit the key entirely when there's no
// signal to pass, so callers can forward TanStack Query's queryFn signal
// straight through without every call site special-casing "no signal yet".
export function withSignal(signal) {
    return {
        init: signal ? { signal, timeoutMs: QUERY_TIMEOUT_MS } : { timeoutMs: QUERY_TIMEOUT_MS },
    };
}
export async function getChallengeWorkspace(client) {
    const res = await client.api.challenges.workspace.$get();
    return jsonOrThrow(res, "GET /api/challenges/workspace");
}
export async function getChallengeHistory(client) {
    const res = await client.api.challenges.history.$get();
    return jsonOrThrow(res, "GET /api/challenges/history");
}
export async function getPastCompanyChallenge(client, challengeId) {
    const res = await client.api.challenges.history[":challengeId"].$get({
        param: { challengeId },
    });
    return jsonOrThrow(res, `GET /api/challenges/history/${challengeId}`);
}
export async function getChallengeWrapUp(client, challengeId) {
    const res = await client.api.challenges.history[":challengeId"]["wrap-up"].$get({
        param: { challengeId },
    });
    return jsonOrThrow(res, `GET /api/challenges/history/${challengeId}/wrap-up`);
}
export async function getChallengePreferences(client, signal) {
    const res = await client.api.challenges.preferences.$get({}, withSignal(signal));
    return jsonOrThrow(res, "GET /api/challenges/preferences");
}
export async function updateChallengePreferences(client, input) {
    const res = await client.api.challenges.preferences.$put({ json: input });
    return jsonOrThrow(res, "PUT /api/challenges/preferences");
}
/**
 * Sets the member's daily step goal. The server decides when it takes effect:
 * a first goal starts today, a change starts tomorrow, so today stays fair.
 */
export async function setStepGoal(client, targetSteps) {
    const res = await client.api.steps.goal.$put({ json: { targetSteps } });
    return jsonOrThrow(res, "PUT /api/steps/goal");
}
/**
 * Marks the member's today as a rest day.
 *
 * Takes no arguments: the date is always the caller's own today, resolved
 * server-side from their timezone, so a rest day cannot be backdated onto a
 * streak that has already broken. A 409 means the allowance is spent; the body
 * carries the refreshed state so the caller can redraw without a refetch.
 */
export async function declareStepRestDay(client) {
    const res = await client.api.steps["rest-day"].$post();
    return jsonOrThrow(res, "POST /api/steps/rest-day");
}
/** Withdraws today's rest day and hands the slot back. */
export async function withdrawStepRestDay(client) {
    const res = await client.api.steps["rest-day"].$delete();
    return jsonOrThrow(res, "DELETE /api/steps/rest-day");
}
export async function getCompanyWalkingChallenge(client) {
    const res = await client.api.challenges.company.$get();
    return jsonOrThrow(res, "GET /api/challenges/company");
}
export async function joinCompanyWalkingChallenge(client, consentVersion) {
    const res = await client.api.challenges.company.join.$post({ json: { consentVersion } });
    return jsonOrThrow(res, "POST /api/challenges/company/join");
}
export async function getCompanyChallengeRouteVote(client) {
    const res = await client.api.challenges.company["next-route-vote"].$get();
    return jsonOrThrow(res, "GET /api/challenges/company/next-route-vote");
}
export async function castCompanyChallengeRouteVote(client, routeInstanceId) {
    const res = await client.api.challenges.company["next-route-vote"].$post({
        json: { routeInstanceId },
    });
    return jsonOrThrow(res, "POST /api/challenges/company/next-route-vote");
}
export async function getChallengeLeaderboardPage(client, instanceId, input) {
    const res = await client.api.challenges.leaderboards[":instanceId"].$get({
        param: { instanceId },
        query: { offset: input.offset.toString(), limit: input.limit.toString() },
    });
    return jsonOrThrow(res, "GET /api/challenges/leaderboards/:instanceId");
}
export async function leaveCompanyWalkingChallenge(client) {
    const res = await client.api.challenges.company.leave.$post();
    return jsonOrThrow(res, "POST /api/challenges/company/leave");
}
export async function getJourneyGroupProgress(client, enrollmentId, signal) {
    const res = await client.api.challenges.enrollments[":enrollmentId"]["group-progress"].$get({ param: { enrollmentId } }, withSignal(signal));
    return jsonOrThrow(res, "GET /api/challenges/enrollments/:enrollmentId/group-progress");
}
export async function getJourneyGroupProgressTimeline(client, enrollmentId, signal) {
    const res = await client.api.challenges.enrollments[":enrollmentId"]["group-progress-timeline"].$get({ param: { enrollmentId } }, withSignal(signal));
    return jsonOrThrow(res, "GET /api/challenges/enrollments/:enrollmentId/group-progress-timeline");
}
export async function claimRouteMilestone(client, input) {
    const res = await client.api.challenges.milestones.claim.$post({ json: input });
    return jsonOrThrow(res, "POST /api/challenges/milestones/claim");
}
export async function claimCompanyChallengeOvertakes(client) {
    const res = await client.api.challenges.overtakes.claim.$post();
    return jsonOrThrow(res, "POST /api/challenges/overtakes/claim");
}
/**
 * Dev replay of the waypoint celebration against the member's real active
 * route. Returns a genuine celebration, so its share button publishes for real.
 */
export async function devReplayRouteWaypoint(client) {
    const res = await client.api.me["dev-route-waypoint"].$post();
    return jsonOrThrow(res, "POST /api/me/dev-route-waypoint");
}
export async function shareRouteMilestone(client, input) {
    const res = await client.api.community["route-milestone"].$post({ json: input });
    return jsonOrThrow(res, "POST /api/community/route-milestone");
}
export async function shareChallengeOvertake(client, eventId) {
    const res = await client.api.community["challenge-overtake"].$post({ json: { eventId } });
    return jsonOrThrow(res, "POST /api/community/challenge-overtake");
}
export async function enrollInChallenge(client, instanceId) {
    const res = await client.api.challenges.enroll.$post({ json: { instanceId } });
    return jsonOrThrow(res, "POST /api/challenges/enroll");
}
export async function getChallengeLeaderboards(client) {
    const res = await client.api.challenges.leaderboards.$get();
    return jsonOrThrow(res, "GET /api/challenges/leaderboards");
}
export async function getChallengeLeaderboardGroup(client, instanceId, groupId, signal) {
    const res = await client.api.challenges["leaderboard-groups"][":instanceId"][":groupId"].$get({ param: { instanceId, groupId } }, withSignal(signal));
    return jsonOrThrow(res, "GET /api/challenges/leaderboard-groups/:instanceId/:groupId");
}
export async function getWalkingGroups(client) {
    const res = await client.api["walking-groups"].$get();
    return jsonOrThrow(res, "GET /api/walking-groups");
}
export async function createWalkingGroup(client, input) {
    const res = await client.api["walking-groups"].$post({ json: input });
    return jsonOrThrow(res, "POST /api/walking-groups");
}
export async function joinWalkingGroup(client, groupId) {
    const res = await client.api["walking-groups"][":groupId"].join.$post({ param: { groupId } });
    return jsonOrThrow(res, "POST /api/walking-groups/:groupId/join");
}
export async function leaveWalkingGroup(client, groupId) {
    const res = await client.api["walking-groups"][":groupId"].leave.$post({ param: { groupId } });
    return jsonOrThrow(res, "POST /api/walking-groups/:groupId/leave");
}
export async function inviteToWalkingGroup(client, groupId, userId) {
    const res = await client.api["walking-groups"][":groupId"].invitations.$post({
        param: { groupId },
        json: { userId },
    });
    await jsonOrThrow(res, "POST /api/walking-groups/:groupId/invitations");
}
export async function respondToWalkingGroupInvitation(client, invitationId, response) {
    const res = await client.api["walking-groups"].invitations[":invitationId"].respond.$post({
        param: { invitationId },
        json: { response },
    });
    await jsonOrThrow(res, "POST /api/walking-groups/invitations/:invitationId/respond");
}
export async function getWalkingGroupMembers(client, groupId, signal) {
    const res = await client.api["walking-groups"][":groupId"].members.$get({ param: { groupId } }, withSignal(signal));
    return jsonOrThrow(res, "GET /api/walking-groups/:groupId/members");
}
export async function uploadWalkingGroupImage(client, groupId, input) {
    const res = await client.api["walking-groups"][":groupId"].image.$post({
        param: { groupId },
        json: input,
    });
    return jsonOrThrow(res, "POST /api/walking-groups/:groupId/image");
}
export async function removeWalkingGroupImage(client, groupId) {
    const res = await client.api["walking-groups"][":groupId"].image.$delete({ param: { groupId } });
    return jsonOrThrow(res, "DELETE /api/walking-groups/:groupId/image");
}
// ─── Shared error handling ───────────────────────────────────────────────────
export class ApiError extends Error {
    status;
    code;
    label;
    responseText;
    responseBody;
    userMessage;
    constructor(message, input) {
        super(message);
        this.name = "ApiError";
        this.status = input.status;
        this.code = input.code ?? null;
        this.label = input.label;
        this.responseText = input.responseText ?? "";
        this.responseBody = input.responseBody;
        this.userMessage = input.userMessage ?? null;
    }
}
export function userFacingApiErrorMessage(error, fallback) {
    if (error instanceof ApiError && error.userMessage)
        return error.userMessage;
    return fallback;
}
/** What the member is told the moment a detection lands. Under clinical review. */
export const SUPPORT_COPY = {
    self_harm: "It sounds like things are heavy right now. If you are in immediate danger, contact your local emergency services. This has been flagged to Thrive's team.",
    bp_crisis: "That blood pressure reading is in a range that needs urgent medical attention. Contact emergency services or seek care now.",
};
let supportListener = null;
/** The server flags a detection on the response; the client shows the member something now. */
export function onSupportSignal(listener) {
    supportListener = listener;
}
/** For responses that never pass through here: streams, blobs, the assistant. */
export function readSupportSignal(res) {
    const support = api.SupportKind.safeParse(res.headers?.get(api.SUPPORT_HEADER));
    if (support.success)
        supportListener?.(support.data);
}
export async function jsonOrThrow(res, label) {
    readSupportSignal(res);
    if (!res.ok) {
        if (res.status === 401) {
            throw new ApiError("Your session has ended. Please sign in again.", {
                status: res.status,
                code: "unauthorized",
                label,
                userMessage: "Your session has ended. Please sign in again.",
            });
        }
        const body = await res.text().catch(() => "");
        const parsedBody = parseJsonObject(body);
        const userMessage = parseUserMessage(parsedBody);
        const code = parseErrorCode(parsedBody);
        throw new ApiError(userMessage ?? `${label} failed: ${res.status}${body ? ` ${body}` : ""}`, {
            status: res.status,
            code,
            label,
            responseText: body,
            responseBody: parsedBody,
            userMessage,
        });
    }
    if (res.status === 204)
        return undefined;
    return res.json();
}
function parseJsonObject(body) {
    if (!body.trim())
        return null;
    try {
        const parsed = JSON.parse(body);
        if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
            return parsed;
        }
    }
    catch {
        return null;
    }
    return null;
}
function parseUserMessage(body) {
    const message = body?.message;
    if (typeof message !== "string")
        return null;
    const trimmed = message.trim();
    return trimmed || null;
}
function parseErrorCode(body) {
    const code = body?.error ?? body?.code;
    if (typeof code !== "string")
        return null;
    const trimmed = code.trim();
    return trimmed || null;
}
// Return null on 404, otherwise jsonOrThrow. Used for endpoints that 404 to
// signal "nothing ready yet" (e.g. the latest brief while pending).
export async function jsonOrNull(res, label) {
    if (res.status === 404)
        return null;
    return jsonOrThrow(res, label);
}
export async function getAmbient(client, options = {}) {
    const query = {};
    if (options.client)
        query.client = options.client;
    const res = await client.api.ambient.$get({ query });
    return jsonOrThrow(res, "GET /api/ambient");
}
export async function consumeAmbient(client, response = {}) {
    const res = await client.api.ambient.consume.$post({ json: response });
    await jsonOrThrow(res, "POST /api/ambient/consume");
}
export async function notNowAmbient(client, payload) {
    const res = await client.api.ambient["not-now"].$post({
        json: payload,
    });
    await jsonOrThrow(res, "POST /api/ambient/not-now");
}
// A member asking for a person. Never refused; it lands in the escalation queue as member.requested.
export async function requestHumanSupport(client, note) {
    const res = await client.api.escalations.$post({
        json: { memberInitiated: true, ...(note ? { note } : {}) },
    });
    return jsonOrThrow(res, "POST /api/escalations");
}
// Submit the fortnightly wellbeing check-in: all five Thrive behaviour
// statements answered together. The server records one survey.pulse signal and
// returns the movement against the previous check-in (or onboarding baseline).
export async function submitWellbeingPulse(client, answers) {
    const res = await client.api.wellbeing.pulse.$post({ json: { answers } });
    return jsonOrThrow(res, "POST /api/wellbeing/pulse");
}
// ─── Onboarding ──────────────────────────────────────────────────────────────
export async function getOnboardingStatus(client) {
    const res = await client.api.onboarding.$get();
    return jsonOrThrow(res, "GET /api/onboarding");
}
export async function updateOnboarding(client, body) {
    const res = await client.api.onboarding.$put({ json: body });
    return jsonOrThrow(res, "PUT /api/onboarding");
}
export async function completeOnboarding(client) {
    const res = await client.api.onboarding.complete.$post();
    return jsonOrThrow(res, "POST /api/onboarding/complete");
}
export async function updateProfileTimezone(client, timezone) {
    const res = await client.api.profile.$put({ json: { timezone } });
    await jsonOrThrow(res, "PUT /api/profile");
}
// ─── Resets ─────────────────────────────────────────────────────────────────────
/**
 * Records that a member played, or finished, a reset.
 *
 * Distinct from consuming the attention card: that fires on the tap, and a tap
 * is not a wind-down. Pass the `decisionId` from the card so the watch can be
 * attributed to the decision that surfaced it; omit it for a reset the member
 * found in the library themselves.
 */
export async function recordResetWatch(client, resetId, body) {
    const res = await client.api.resets[":resetId"].watch.$post({
        param: { resetId },
        json: body,
    });
    return jsonOrThrow(res, "POST /api/resets/:resetId/watch");
}
/** The hold currently on pushes, if any. Null once it has expired. */
export async function getPushSnooze(client) {
    const res = await client.api.ambient["push-snooze"].$get();
    return jsonOrThrow(res, "GET /api/ambient/push-snooze");
}
export async function sendFeedback(client, feedback) {
    const res = await client.api.signals.feedback.$post({
        json: feedback,
    });
    await jsonOrThrow(res, "POST /api/signals/feedback");
}
export async function getSignalTimeline(client, options) {
    const res = await client.api.signals.timeline.$get({
        query: {
            hours: String(options?.hours ?? 48),
            limit: String(options?.limit ?? 200),
            ...(options?.until ? { until: options.until } : {}),
        },
    });
    return jsonOrThrow(res, "GET /api/signals/timeline");
}
// ─── Logbook ─────────────────────────────────────────────────────────────────
/** The hero chart + stat tile behind a Logbook tab, for one pillar/day-range. */
export async function getLogbookTrend(client, options, signal) {
    const res = await client.api.signals.logbook.trend.$get({
        query: {
            pillar: options.pillar,
            days: options.days,
            ...(options.until ? { until: options.until } : {}),
            ...(options.date ? { date: options.date } : {}),
        },
    }, withSignal(signal));
    return jsonOrThrow(res, "GET /api/signals/logbook/trend");
}
/** One page of a Logbook tab's flat, paginated history list. */
export async function getLogbookHistory(client, options, signal) {
    const res = await client.api.signals.logbook.$get({
        query: {
            ...(options.pillar ? { pillar: options.pillar } : {}),
            ...(options.source ? { source: options.source } : {}),
            ...(options.type ? { type: options.type } : {}),
            ...(options.date ? { date: options.date } : {}),
            ...(options.cursor ? { cursor: options.cursor } : {}),
            ...(options.limit ? { limit: String(options.limit) } : {}),
        },
    }, withSignal(signal));
    return jsonOrThrow(res, "GET /api/signals/logbook");
}
export async function recordManualSignalEntry(client, body) {
    const res = await client.api.signals.manual.$post({ json: body });
    return jsonOrThrow(res, "POST /api/signals/manual");
}
/**
 * "Did you feel rested?" is a sleep quality note, not a mood check-in. Sent as
 * a mood with an invented energy score it fed a number nobody reported into
 * scoring and stood in for the day's real mood check-in.
 */
export async function recordRestedAnswer(client, rested) {
    return recordManualSignalEntry(client, {
        type: "sleep",
        sleepKind: "quality_note",
        quality: rested ? "good" : "poor",
        label: rested ? "Felt rested this morning" : "Did not feel rested this morning",
    });
}
export async function transcribeManualVoiceNote(client, body) {
    const res = await client.api.signals.manual.transcribe.$post({ json: body });
    return jsonOrThrow(res, "POST /api/signals/manual/transcribe");
}
export async function analyzeManualFoodPhoto(client, body) {
    const res = await client.api.signals.manual["food-analysis"].$post({ json: body });
    return jsonOrThrow(res, "POST /api/signals/manual/food-analysis");
}
/**
 * One manual entry's raw `type`/`payload`, resolved directly by
 * `manualEntryId` (no time window, no row-count cap); the fix for
 * `onEditEntry`'s (`use-logbook-tab-data.ts`) old `getSignalTimeline`
 * scan, which could silently miss an entry outside that window's cap.
 */
export async function getManualSignalEntry(client, manualEntryId) {
    const res = await client.api.signals.manual[":id"].$get({
        param: { id: manualEntryId },
    });
    return jsonOrThrow(res, "GET /api/signals/manual/:id");
}
export async function updateManualSignalEntry(client, manualEntryId, body) {
    const res = await client.api.signals.manual[":id"].$put({
        param: { id: manualEntryId },
        json: body,
    });
    return jsonOrThrow(res, "PUT /api/signals/manual/:id");
}
export async function deleteManualSignalEntry(client, manualEntryId) {
    const res = await client.api.signals.manual[":id"].$delete({
        param: { id: manualEntryId },
    });
    return jsonOrThrow(res, "DELETE /api/signals/manual/:id");
}
// ─── Content ──────────────────────────────────────────────────────────────────
/** Strip plan item kind prefixes before calling GET /api/content/article/:id. */
export function contentArticleId(rawId) {
    const colonIndex = rawId.indexOf(":");
    if (colonIndex === -1)
        return rawId;
    const id = rawId.slice(colonIndex + 1).trim();
    return id.length > 0 ? id : rawId;
}
// ─── Weekly Brief ───────────────────────────────────────────────────────────
// Returns the latest brief, or null when none is ready yet (the route 404s for
// pending/failed briefs).
export async function getTodayBrief(client) {
    const res = await client.api.brief.today.$get();
    return jsonOrNull(res, "GET /api/brief/today");
}
export async function getBriefHistory(client) {
    const res = await client.api.brief.history.$get();
    return jsonOrThrow(res, "GET /api/brief/history");
}
/** Starts a brief's narration if it is not rendered yet, and says whether it is ready to play. */
export async function prepareBriefAudio(client, briefId, signal) {
    const res = await client.api.brief.audio[":id"].prepare.$post({ param: { id: briefId } }, withSignal(signal));
    return jsonOrThrow(res, "POST /api/brief/audio/:id/prepare");
}
/** Records that the member finished listening to the latest brief. Idempotent. */
export async function recordBriefPlayed(client) {
    const res = await client.api.brief.today.played.$post();
    return jsonOrThrow(res, "POST /api/brief/today/played");
}
// ─── Library ─────────────────────────────────────────────────────────────────
export async function getLibraryItems(client, query) {
    const res = query
        ? await client.api.library.items.$get({
            query: {
                ...(query.query ? { query: query.query } : {}),
                ...(query.kind ? { kind: query.kind } : {}),
                ...(query.behaviours?.length ? { behaviours: query.behaviours.join(",") } : {}),
                ...(query.tags?.length ? { tags: query.tags.join(",") } : {}),
                ...(query.locale ? { locale: query.locale } : {}),
                ...(query.page ? { page: String(query.page) } : {}),
                ...(query.limit ? { limit: String(query.limit) } : {}),
            },
        })
        : await client.api.library.items.$get({ query: {} });
    return jsonOrThrow(res, "GET /api/library/items");
}
/** A Thrive CMS collection's items in editorial order ("Breathing Techniques", "Bedtime Stories"). */
export async function getLibraryCollection(client, collectionId, query = {}) {
    const res = await client.api.library.collections[":collectionId"].$get({
        param: { collectionId },
        query: {
            ...(query.page ? { page: String(query.page) } : {}),
            ...(query.limit ? { limit: String(query.limit) } : {}),
        },
    });
    return jsonOrThrow(res, "GET /api/library/collections/:collectionId");
}
/** The CMS pillar hubs (Sleep, Stress, ...) and their collections. */
export async function getLibraryHubs(client) {
    const res = await client.api.library.hubs.$get();
    return jsonOrThrow(res, "GET /api/library/hubs");
}
/** Every course the member may open, each with their progress. */
export async function getCourses(client) {
    const res = await client.api.courses.$get();
    return jsonOrThrow(res, "GET /api/courses");
}
/** One course's lessons and videos, with the member's progress through each. */
export async function getCourse(client, courseId) {
    const res = await client.api.courses[":courseId"].$get({ param: { courseId } });
    return jsonOrThrow(res, "GET /api/courses/:courseId");
}
/** Records where the member is in one course video. */
export async function saveCourseModuleProgress(client, courseId, moduleId, body) {
    const res = await client.api.courses[":courseId"].modules[":moduleId"].progress.$put({
        param: { courseId, moduleId },
        json: body,
    });
    return jsonOrThrow(res, "PUT /api/courses/:courseId/modules/:moduleId/progress");
}
export async function getSnapshot(client, signal) {
    const res = await client.api.me.snapshot.$get({ query: {} }, withSignal(signal));
    return jsonOrThrow(res, "GET /api/me/snapshot");
}
export async function getSnapshotDetail(client) {
    const res = await client.api.me.snapshot.detail.$get();
    return jsonOrThrow(res, "GET /api/me/snapshot/detail");
}
/** One day of Snapshot: steps, sleep, the other readings, and the brief if one was composed. */
export async function getSnapshotDay(client, date, signal) {
    const res = await client.api.me.snapshot.day.$get({ query: { date } }, withSignal(signal));
    return jsonOrThrow(res, "GET /api/me/snapshot/day");
}
/** The day picker's range: goal results, rest days, entries and briefs per day, plus the streak. */
export async function getSnapshotCalendar(client, range, signal) {
    const res = await client.api.me.snapshot.calendar.$get({ query: range }, withSignal(signal));
    return jsonOrThrow(res, "GET /api/me/snapshot/calendar");
}
export async function getSnapshotGoal(client) {
    const res = await client.api.me.snapshot.goal.$get();
    return jsonOrThrow(res, "GET /api/me/snapshot/goal");
}
export async function updateSnapshotGoal(client, body) {
    const res = await client.api.me.snapshot.goal.$put({ json: body });
    return jsonOrThrow(res, "PUT /api/me/snapshot/goal");
}
export async function getLibraryRecommendations(client) {
    const res = await client.api.library.recommendations.$get();
    return jsonOrThrow(res, "GET /api/library/recommendations");
}
export async function rateLibraryContent(client, contentId, vote) {
    const res = await client.api.library.items[":contentId"].rating.$put({
        param: { contentId },
        json: { vote },
    });
    return jsonOrThrow(res, "PUT /api/library/items/:contentId/rating");
}
export async function fetchContentCompletions(client, contentId) {
    const res = await client.api.library.content[":contentId"].completions.$get({
        param: { contentId },
    });
    return jsonOrThrow(res, "GET /api/library/content/:contentId/completions");
}
export async function getUserContentCompletions(client, kind) {
    const res = await client.api.library.completions.$get({
        query: kind ? { kind } : {},
    });
    return jsonOrThrow(res, "GET /api/library/completions");
}
export async function createContentCompletion(client, contentId, body) {
    const res = await client.api.library.content[":contentId"].completions.$post({
        param: { contentId },
        json: body,
    });
    return jsonOrThrow(res, "POST /api/library/content/:contentId/completions");
}
export async function scheduleReminder(client, reminder) {
    const res = await client.api.reminders.$post({ json: reminder });
    return jsonOrThrow(res, "POST /api/reminders");
}
// ─── Coaching (coachee) ─────────────────────────────────────────────────────
/** A member's own routes for one kind of programme: coaching, or their care team under /api/care. */
const mine = (client, kind = "coaching") => (kind === "care_team" ? client.api.care : client.api.coaching);
export async function getCoaching(client, kind = "coaching") {
    return jsonOrThrow(await mine(client, kind).$get(), "GET coaching");
}
/** The programmes a member has now, at most one of each kind. */
export async function getCoachingProgrammes(client) {
    return jsonOrThrow(await client.api.coaching.programmes.$get(), "GET coaching programmes");
}
export async function getCoachingSlots(client, kind = "coaching") {
    return jsonOrThrow(await mine(client, kind).slots.$get(), "GET coaching slots");
}
export async function bookCoachingSession(client, startsAt, kind = "coaching") {
    await jsonOrThrow(await mine(client, kind).sessions.$post({ json: { startsAt } }), "POST coaching session");
}
/** Move and cancel are the same call for a coachee and their coach, under each one's own prefix. */
const sessionsAs = (client, as) => as === "coach" ? client.api.coaching.coach.sessions : client.api.coaching.sessions;
export async function moveCoachingSession(client, id, startsAt, as = "coachee") {
    await jsonOrThrow(await sessionsAs(client, as)[":id"].move.$post({ param: { id }, json: { startsAt } }), "POST coaching session move");
}
export async function cancelCoachingSession(client, id, as = "coachee") {
    await jsonOrThrow(await sessionsAs(client, as)[":id"].cancel.$post({ param: { id } }), "POST coaching session cancel");
}
/** The relationship routes from either side; both mount the same handlers, and a coachee's path ignores `id`. */
const relation = (client, scope) => (typeof scope === "string"
    ? client.api.coaching.coach.coachees[":id"]
    : mine(client, scope?.kind));
const rel = (scope) => ({ id: typeof scope === "string" ? scope : "" });
export async function getCoachingThread(client, scope) {
    return jsonOrThrow(await relation(client, scope).messages.$get({ param: rel(scope) }), "GET coaching thread");
}
/** Marks the other person's messages as seen, once the thread is actually on screen. */
export async function markCoachingThreadRead(client, scope) {
    await jsonOrThrow(await relation(client, scope).messages.read.$post({ param: rel(scope) }), "POST coaching thread read");
}
export async function sendCoachingMessage(client, body, scope) {
    await jsonOrThrow(await relation(client, scope).messages.$post({ param: rel(scope), json: { body } }), "POST coaching message");
}
export async function getCoachingNotes(client, scope) {
    return jsonOrThrow(await relation(client, scope).notes.$get({ param: rel(scope) }), "GET coaching notes");
}
export async function addCoachingNote(client, note, scope) {
    await jsonOrThrow(await relation(client, scope).notes.$post({ param: rel(scope), json: note }), "POST coaching note");
}
export async function editCoachingNote(client, noteId, body, scope) {
    await jsonOrThrow(await relation(client, scope).notes[":itemId"].$patch({
        param: { ...rel(scope), itemId: noteId },
        json: { body },
    }), "PATCH coaching note");
}
export async function removeCoachingNote(client, noteId, scope) {
    await jsonOrThrow(await relation(client, scope).notes[":itemId"].$delete({
        param: { ...rel(scope), itemId: noteId },
    }), "DELETE coaching note");
}
export async function getCoachingActions(client, scope) {
    return jsonOrThrow(await relation(client, scope).actions.$get({ param: rel(scope) }), "GET coaching actions");
}
export async function addCoachingAction(client, action, scope) {
    await jsonOrThrow(await relation(client, scope).actions.$post({ param: rel(scope), json: action }), "POST coaching action");
}
export async function closeCoachingAction(client, actionId, status, scope) {
    await jsonOrThrow(await relation(client, scope).actions[":itemId"].close.$post({
        param: { ...rel(scope), itemId: actionId },
        json: { status },
    }), "POST coaching action close");
}
export async function answerCoachingAction(client, actionId, outcome, kind = "coaching") {
    await jsonOrThrow(await mine(client, kind).actions[":itemId"].answer.$post({
        param: { itemId: actionId },
        json: { outcome },
    }), "POST coaching action answer");
}
export async function setCoachingSharing(client, sharing, kind = "coaching") {
    await jsonOrThrow(await mine(client, kind).sharing.$put({ json: { sharing } }), "PUT coaching sharing");
}
export async function markCoachingSeen(client, kind = "coaching") {
    await jsonOrThrow(await mine(client, kind).seen.$post(), "POST coaching seen");
}
/** A coach reads their coachee's pre-read; a coachee, passing no id, reads their own week. */
export async function getCoachPreRead(client, scope) {
    return jsonOrThrow(await relation(client, scope)["pre-read"].$get({ param: rel(scope) }), "GET coaching pre-read");
}
/** Joins the programme a member was invited to, from the invite link or by their address. */
export async function joinCoaching(client, inviteId) {
    return jsonOrThrow(await client.api.coaching.join.$post({ json: { inviteId } }), "POST coaching join");
}
/** Opens a questionnaire the member may start themselves, such as a daily check-in; returns its id to answer. */
export async function startCoachingAssessment(client, kind, programme = "coaching") {
    const done = await jsonOrThrow(await mine(client, programme).assessments.start.$post({ json: { kind } }), "POST coaching assessment start");
    return done.id;
}
export async function completeCoachingAssessment(client, id, answers, programme = "coaching") {
    await jsonOrThrow(await mine(client, programme).assessments[":itemId"].$post({
        param: { itemId: id },
        json: { answers },
    }), "POST coaching assessment");
}
// ─── Coaching (coach) ───────────────────────────────────────────────────────
/** The coachee of theirs a full email address belongs to, as an id. */
export async function searchCoacheesByEmail(client, q, signal) {
    const res = await client.api.coaching.coach.coachees.search.$get({ query: { q } }, withSignal(signal));
    const done = await jsonOrThrow(res, "GET coachee search");
    return done.ids;
}
/** A coach's unread messages across their coachees, for the nav badge. */
export async function getCoachUnreadCount(client) {
    const done = await jsonOrThrow(await client.api.coaching.coach.unread.$get(), "GET coach unread");
    return done.unread;
}
export async function getCoachees(client) {
    return jsonOrThrow(await client.api.coaching.coach.coachees.$get(), "GET coach coachees");
}
export async function getCoachee(client, id) {
    return jsonOrThrow(await client.api.coaching.coach.coachees[":id"].$get({ param: { id } }), "GET coach coachee");
}
export async function getCoacheeSlots(client, id) {
    return jsonOrThrow(await relation(client, id).slots.$get({ param: rel(id) }), "GET coachee slots");
}
export async function bookForCoachee(client, id, startsAt) {
    await jsonOrThrow(await relation(client, id).sessions.$post({ param: rel(id), json: { startsAt } }), "POST coach booking");
}
export async function getCoacheeAssessments(client, id) {
    return jsonOrThrow(await relation(client, id).assessments.$get({ param: rel(id) }), "GET coachee assessments");
}
export async function sendCoachingAssessment(client, id, kind) {
    await jsonOrThrow(await relation(client, id).assessments.$post({ param: rel(id), json: { kind } }), "POST coachee assessment");
}
export async function getCoachSessions(client, from, to) {
    return jsonOrThrow(await client.api.coaching.coach.sessions.$get({ query: { from, to } }), "GET coach sessions");
}
export async function updateCoachingSession(client, id, patch) {
    await jsonOrThrow(await client.api.coaching.coach.sessions[":id"].$patch({ param: { id }, json: patch }), "PATCH coach session");
}
export async function getCoachAttention(client) {
    return jsonOrThrow(await client.api.coaching.coach.attention.$get(), "GET coach attention");
}
export async function setCoachAttentionAside(client, itemId, until) {
    await jsonOrThrow(await client.api.coaching.coach.attention[":itemId"].aside.$post({
        param: { itemId },
        json: { until },
    }), "POST coach attention aside");
}
export async function restoreCoachAttention(client, itemId) {
    await jsonOrThrow(await client.api.coaching.coach.attention[":itemId"].aside.$delete({ param: { itemId } }), "DELETE coach attention aside");
}
export async function markCoachAssessmentsReviewed(client, coacheeId) {
    await jsonOrThrow(await relation(client, coacheeId).assessments.reviewed.$post({ param: rel(coacheeId) }), "POST coach assessments reviewed");
}
export async function getCoachSettings(client) {
    return jsonOrThrow(await client.api.coaching.coach.settings.$get(), "GET coach settings");
}
export async function saveCoachSettings(client, settings) {
    await jsonOrThrow(await client.api.coaching.coach.settings.$put({ json: settings }), "PUT coach settings");
}
export async function uploadCoachPhoto(client, photo) {
    await jsonOrThrow(await client.api.coaching.coach.photo.$post({ json: photo }), "POST coach photo");
}
export async function removeCoachPhoto(client) {
    await jsonOrThrow(await client.api.coaching.coach.photo.$delete(), "DELETE coach photo");
}
// ─── Coaching (Coaching Ops) ────────────────────────────────────────────────
export async function getOpsAttention(client, kind) {
    return jsonOrThrow(await client.api.coaching.ops.attention.$get({ query: { kind } }), "GET coaching ops attention");
}
export async function setOpsCoachAccepting(client, coachId, accepting) {
    await jsonOrThrow(await client.api.coaching.ops.coaches[":id"].accepting.$put({
        param: { id: coachId },
        json: { accepting },
    }), "PUT coaching ops coach accepting");
}
/** Ends a programme now and cancels what was still booked; returns how many sessions that was. */
export async function endCoachingEnrollment(client, enrollmentId) {
    const done = await jsonOrThrow(await client.api.coaching.ops.enrollments[":id"].end.$post({ param: { id: enrollmentId } }), "POST coaching ops end enrolment");
    return done.cancelled;
}
export async function getCoachingCompanies(client) {
    return jsonOrThrow(await client.api.coaching.ops.companies.$get(), "GET coaching ops companies");
}
export async function getCoachingCohorts(client, kind) {
    return jsonOrThrow(await client.api.coaching.ops.cohorts.$get({ query: { kind } }), "GET coaching ops cohorts");
}
/** Puts a coach or clinician on a company programme; its unmatched members get them too. */
export async function setCohortCoach(client, cohortId, coachUserId) {
    return jsonOrThrow(await client.api.coaching.ops.cohorts[":id"].coach.$put({
        param: { id: cohortId },
        json: { coachUserId },
    }), "PUT coaching ops cohort coach");
}
export async function createCoachingCohort(client, body) {
    const done = await jsonOrThrow(await client.api.coaching.ops.cohorts.$post({ json: body }), "POST coaching ops cohort");
    return done.id;
}
export async function getCoachingInvites(client, cohortId) {
    return jsonOrThrow(await client.api.coaching.ops.cohorts[":id"].invites.$get({ param: { id: cohortId } }), "GET coaching ops invites");
}
export async function sendCoachingInvites(client, cohortId, emails) {
    return jsonOrThrow(await client.api.coaching.ops.cohorts[":id"].invites.$post({
        param: { id: cohortId },
        json: { emails },
    }), "POST coaching ops invites");
}
export async function resendCoachingInvite(client, inviteId) {
    await jsonOrThrow(await client.api.coaching.ops.invites[":id"].resend.$post({ param: { id: inviteId } }), "POST coaching ops invite resend");
}
export async function withdrawCoachingInvite(client, inviteId) {
    await jsonOrThrow(await client.api.coaching.ops.invites[":id"].withdraw.$post({ param: { id: inviteId } }), "POST coaching ops invite withdraw");
}
/** One page of Coaching Ops' coachees, searched, filtered and sorted on the server. */
export async function getOpsCoachees(client, query, signal) {
    const q = Object.fromEntries(Object.entries(query).flatMap(([k, v]) => v === undefined || v === "" ? [] : [[k, String(v)]]));
    return jsonOrThrow(await client.api.coaching.ops.coachees.$get({ query: q }, withSignal(signal)), "GET coaching ops coachees");
}
export async function getOpsCoachee(client, id) {
    return jsonOrThrow(await client.api.coaching.ops.coachees[":id"].$get({ param: { id } }), "GET coaching ops coachee");
}
export async function getOpsCoachingOverview(client, kind) {
    return jsonOrThrow(await client.api.coaching.ops.overview.$get({ query: { kind } }), "GET coaching ops overview");
}
export async function getCoachRoster(client) {
    return jsonOrThrow(await client.api.coaching.ops.coaches.$get(), "GET coaching ops coaches");
}
export async function searchCoachingMembers(client, q) {
    return jsonOrThrow(await client.api.coaching.ops.members.$get({ query: { q } }), "GET coaching ops members");
}
export async function saveEnrollment(client, coacheeId, enrollment) {
    await jsonOrThrow(await client.api.coaching.ops.enrollments[":id"].$put({
        param: { id: coacheeId },
        json: enrollment,
    }), "PUT coaching enrollment");
}
// ─── Terra ────────────────────────────────────────────────────────────────────
export async function getTerraToken(client) {
    const res = await client.api.connections.terra["mobile-token"].$get();
    return jsonOrThrow(res, "GET /api/connections/terra/mobile-token");
}
export async function recordAppleHealthConnection(client, terraUserId) {
    const res = await client.api.connections.terra["apple-health"].$post({
        json: { terraUserId },
    });
    await jsonOrThrow(res, "POST /api/connections/terra/apple-health");
}
/** The Android device-health counterpart of recordAppleHealthConnection. */
export async function recordHealthConnectConnection(client, terraUserId) {
    const res = await client.api.connections.terra["health-connect"].$post({
        json: { terraUserId },
    });
    await jsonOrThrow(res, "POST /api/connections/terra/health-connect");
}
export async function getTerraAuthUrl(client, resource, target = "mobile") {
    const query = { resource, target };
    const res = await client.api.connections.terra.auth.$get({
        query,
    });
    return jsonOrThrow(res, "GET /api/connections/terra/auth");
}
// ─── Weekly reflection ────────────────────────────────────────────────────────
// ─── Connections ──────────────────────────────────────────────────────────────
export async function getConnections(client) {
    const res = await client.api.connections.$get();
    return jsonOrThrow(res, "GET /api/connections");
}
export async function getGoogleCalendarConnectUrl(client, target = "mobile") {
    const res = await client.api.connections.google.connect.$get({ query: { target } });
    return jsonOrThrow(res, "GET /api/connections/google/connect");
}
export async function getMicrosoftCalendarConnectUrl(client, target = "mobile") {
    const res = await client.api.connections.microsoft.connect.$get({ query: { target } });
    return jsonOrThrow(res, "GET /api/connections/microsoft/connect");
}
export async function syncGoogleCalendar(client) {
    const res = await client.api.connections.google.sync.$post();
    return jsonOrThrow(res, "POST /api/connections/google/sync");
}
export async function recordMobileCompanionConnection(client) {
    const res = await client.api.connections["mobile-companion"].$post();
    await jsonOrThrow(res, "POST /api/connections/mobile-companion");
}
// ─── Identity ────────────────────────────────────────────────────────────────
// The caller's own identity and onboarding state. Read at boot for the values a
// client cannot derive locally: the covered entity it belongs to (for telemetry
// attribution) and the server-authoritative onboarding day zero.
export async function getWhoami(client) {
    const res = await client.api.me.whoami.$get();
    return jsonOrThrow(res, "GET /api/me/whoami");
}
// Disconnect a wearable/integration (also deauthenticates the Terra user
// server-side). Used to disconnect Apple Health from the mobile app.
export async function disconnectConnection(client, provider) {
    const res = await client.api.connections[":provider"].$delete({
        param: { provider },
    });
    await jsonOrThrow(res, `DELETE /api/connections/${provider}`);
}
// ─── Personal Resets (mobile-only for now) ─────────────────────────────────
export async function listMyPersonalResets(client) {
    const res = await client.api["personal-resets"].mine.$get();
    return jsonOrThrow(res, "GET /api/personal-resets/mine");
}
export async function getResetAudioLibrary(client, genres, page) {
    const res = await client.api["personal-resets"]["audio-library"].$get({
        query: {
            ...(genres ? { genres } : {}),
            ...(page && page > 1 ? { page: String(page) } : {}),
        },
    });
    return jsonOrThrow(res, "GET /api/personal-resets/audio-library");
}
export async function listSharedPersonalResets(client) {
    const res = await client.api["personal-resets"].shared.$get();
    return jsonOrThrow(res, "GET /api/personal-resets/shared");
}
export async function listCommunityPersonalResets(client) {
    const res = await client.api["personal-resets"].community.$get();
    return jsonOrThrow(res, "GET /api/personal-resets/community");
}
export async function getPersonalReset(client, id) {
    const res = await client.api["personal-resets"][":id"].$get({ param: { id } });
    return jsonOrThrow(res, `GET /api/personal-resets/${id}`);
}
export async function createPersonalReset(client, body) {
    const res = await client.api["personal-resets"].$post({ json: body });
    return jsonOrThrow(res, "POST /api/personal-resets");
}
export async function updatePersonalReset(client, id, body) {
    const res = await client.api["personal-resets"][":id"].$put({ param: { id }, json: body });
    return jsonOrThrow(res, `PUT /api/personal-resets/${id}`);
}
export async function deletePersonalReset(client, id) {
    const res = await client.api["personal-resets"][":id"].$delete({ param: { id } });
    return jsonOrThrow(res, `DELETE /api/personal-resets/${id}`);
}
export async function sharePersonalReset(client, id, body) {
    const res = await client.api["personal-resets"][":id"].share.$post({ param: { id }, json: body });
    return jsonOrThrow(res, `POST /api/personal-resets/${id}/share`);
}
/** The live attention queue; mobile reads broadcasts from it, web streams it. */
export async function getAttentionEvents(client) {
    const res = await client.api.attention.$get();
    return jsonOrThrow(res, "GET /api/attention");
}
// Reported once when a surface actually puts the card on screen. Fire and
// forget: a lost report costs one row's exposure flag, never the member's card.
export async function reportMomentSeen(client, id) {
    await client.api.moments[":id"].seen.$post({ param: { id } });
}
export async function recordMomentOutcome(client, id, outcome) {
    const res = await client.api.moments[":id"].outcome.$post({ param: { id }, json: { outcome } });
    return jsonOrThrow(res, "POST /api/moments/:id/outcome");
}
export async function getContactCentreMe(client) {
    const res = await client.api["contact-centre"].me.$get();
    if (res.status === 403)
        return null;
    return jsonOrThrow(res, "GET /api/contact-centre/me");
}
export async function getContactCentreReset(client) {
    const res = await client.api["contact-centre"].reset.$get();
    if (res.status === 403) {
        return { id: null, videoUrl: null };
    }
    return jsonOrThrow(res, "GET /api/contact-centre/reset");
}
//# sourceMappingURL=api.js.map